import { useState } from "react";
import style from "./pickers.module.css";
import { useMondayBoards } from "frontend/hooks/use-monday-boards-picker";
import { MondayBoard } from "shared/integrations/mapper";
import classNames from "classnames";
import { useDebounce } from "usehooks-ts";
import * as Select from "frontend/ui-components/select/select";
import { concat, prop, uniqBy } from "rambda";

function Placeholder({
  text,
  disabled,
  theme = "light",
}: {
  text: string;
  disabled?: boolean;
  theme?: "light" | "dark";
}) {
  return <div className={classNames(style.placeholder, style[theme], disabled && style.disabled)}>{text}</div>;
}

export function BoardPicker({
  integrationId,
  selectedBoard,
  onBoardSelected,
  includeSubitems,
  theme = "light",
}: {
  integrationId: string;
  selectedBoard?: MondayBoard;
  onBoardSelected: (board: MondayBoard) => void;
  includeSubitems?: boolean;
  theme?: "light" | "dark";
}) {
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, 300);
  const { data: myRecentBoards } = useMondayBoards(integrationId, "", includeSubitems);
  const { data: boards, isLoading } = useMondayBoards(integrationId, debouncedSearch, includeSubitems);

  //Note: Monday's api for getting board is weird. I can get my last-used boards, but if I add search-query
  // I can't get it by this order anymore. The experience for the user is very jarring, so we
  // always show (and filter) the last-used boards first.
  // To do it right we combine the lists, remove duplicates, filter by search-term.
  let allBoards: MondayBoard[] = uniqBy(prop("id"), concat(myRecentBoards ?? [], boards ?? []));
  if (searchValue) {
    const lowercasedSearch = searchValue.toLowerCase();
    allBoards = allBoards.filter((board) => board.name.toLowerCase().includes(lowercasedSearch));
  }

  return (
    <Select.Select
      selectedId={selectedBoard?.id || ""}
      setSelectedId={(id) => {
        const board = allBoards.find((board) => board.id == id);
        board && onBoardSelected(board);
      }}
    >
      <Select.Trigger customClass={classNames(style.selectTrigger, style[theme])}>
        {selectedBoard ? (
          <div
            title={selectedBoard.name}
            style={{ display: "inline-block", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}
          >
            {selectedBoard.name}
          </div>
        ) : isLoading ? (
          <Placeholder text="Loading..." disabled={true} theme={theme} />
        ) : (
          <Placeholder text="Choose board" theme={theme} />
        )}
        <Select.Arrow theme={theme} />
      </Select.Trigger>
      <Select.Popover className={classNames(style.background, style.popover, style.rounded, style[theme])}>
        <Select.Search
          rounded={true}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          className={style.searchField}
        />
        <Select.OptionsList>
          {includeSubitems &&
            allBoards.length === 0 &&
            (isLoading ? (
              <span className={classNames(style.boardPickerOption, style.loading)}>Loading...</span>
            ) : (
              <div className={classNames(style.boardPickerEmptyOption, style.loading)}>
                No boards with subitems found
              </div>
            ))}
          {allBoards.length > 0 && (
            <div className={style.popoverChildren}>
              {allBoards.map((board) => (
                <Select.Option id={board.id} key={`first-${board.id}`}>
                  <div
                    title={board.name}
                    className={classNames(style.boardPickerOption, selectedBoard?.id == board.id && style.selected)}
                  >
                    {board.name}
                  </div>
                </Select.Option>
              ))}
              {isLoading && <span className={classNames(style.boardPickerOption, style.loading)}>Loading...</span>}
            </div>
          )}
        </Select.OptionsList>
      </Select.Popover>
    </Select.Select>
  );
}
